import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { ICON_STROKE } from '../lib/icons.js'

export function Screen({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  // `min-h-0` est indispensable : sans lui, un enfant en `overflow-y-auto`
  // ne peut pas se contraindre dans un conteneur flex et le pied d'écran
  // repart au fil du défilement.
  return <div className={`flex min-h-0 flex-1 flex-col ${className}`}>{children}</div>
}

/** Zone défilante. Tout le contenu d'un écran va ici. */
export function ScreenBody({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`min-h-0 flex-1 overflow-y-auto px-5 ${className}`}>{children}</div>
  )
}

/**
 * Pied d'écran : hors de la zone défilante, donc TOUJOURS visible.
 * C'est là que vit l'action principale — elle ne doit jamais demander de
 * faire défiler pour être découverte.
 */
export function ScreenFooter({
  children,
  safe = false,
}: {
  children: ReactNode
  safe?: boolean
}) {
  return (
    <div className={`bg-canvas px-5 pt-3 pb-3 ${safe ? 'safe-bottom' : ''}`}>
      {children}
    </div>
  )
}

export function Card({
  children,
  className = '',
  tone = 'surface',
}: {
  children: ReactNode
  className?: string
  tone?: 'surface' | 'green' | 'sunken'
}) {
  const tones = {
    surface: 'card',
    // Aplats sans ombre : ils se détachent déjà par la couleur.
    green: 'bg-green-soft rounded-[var(--radius-card)]',
    sunken: 'bg-sunken/60 rounded-[var(--radius-card)]',
  }
  return <div className={`p-5 ${tones[tone]} ${className}`}>{children}</div>
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger'
  full?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'primary',
  full = false,
  icon,
  className = '',
  children,
  ...props
}: ButtonProps) {
  // 56 px de haut, bien au-dessus du minimum tactile de 44 pt.
  const base =
    'press text-body inline-flex min-h-14 items-center justify-center gap-2 rounded-full px-6 font-bold disabled:pointer-events-none disabled:opacity-40'
  const variants = {
    primary: 'bg-green text-surface',
    secondary: 'bg-surface text-ink shadow-[var(--shadow-soft)]',
    danger: 'text-danger',
  }
  return (
    <button
      className={`${base} ${variants[variant]} ${full ? 'w-full' : ''} ${className}`}
      {...props}
    >
      {icon}
      {children}
    </button>
  )
}

/** Bouton rond d'en-tête (retour, raccourci). */
export function IconButton({
  icon: Icon,
  label,
  onClick,
  tone = 'surface',
}: {
  icon: LucideIcon
  label: string
  onClick: () => void
  tone?: 'surface' | 'accent'
}) {
  const tones = {
    surface: 'bg-surface text-ink shadow-[var(--shadow-soft)]',
    accent: 'bg-accent text-ink',
  }
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={`press grid size-12 shrink-0 place-items-center rounded-full ${tones[tone]}`}
    >
      <Icon size={20} strokeWidth={ICON_STROKE} aria-hidden="true" />
    </button>
  )
}

export function Bar({
  progress,
  tone = 'green',
  height = 12,
  label,
}: {
  progress: number
  tone?: 'accent' | 'green'
  height?: number
  label?: string
}) {
  const colors = { accent: 'bg-accent', green: 'bg-green' }
  const percent = Math.min(100, Math.max(0, progress * 100))
  return (
    <div
      className="bg-sunken w-full overflow-hidden rounded-full"
      style={{ height }}
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-500 ease-out ${colors[tone]}`}
        // Un début de progression reste visible : jamais un rail qui paraît vide.
        style={{ width: `${percent}%`, minWidth: percent > 0 ? height : 0 }}
      />
    </div>
  )
}

export function SectionTitle({
  children,
  action,
}: {
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-title font-bold">{children}</h2>
      {action}
    </div>
  )
}

/** Un chiffre et son unité en petit, collée derrière. */
export function Figure({
  value,
  unit,
  size = 'title',
}: {
  value: string | number
  unit?: string
  size?: 'title' | 'display'
}) {
  return (
    <p className={`num font-bold ${size === 'display' ? 'text-display' : 'text-title'}`}>
      {value}
      {unit && (
        <span className={`ml-0.5 ${size === 'display' ? 'text-title' : 'text-caption'}`}>
          {unit}
        </span>
      )}
    </p>
  )
}

/** Tuile de statistique : libellé discret, chiffre dominant. */
export function StatTile({
  label,
  value,
  unit,
  icon: Icon,
  tone = 'green',
  aside,
}: {
  label: string
  value: string | number
  unit?: string
  icon?: LucideIcon
  tone?: 'green' | 'accent' | 'lilac'
  aside?: ReactNode
}) {
  const tones = {
    green: 'bg-green-soft',
    accent: 'bg-accent-soft',
    lilac: 'bg-lilac-soft',
  }
  return (
    <div className="card flex min-w-0 items-end justify-between gap-2 p-4">
      <div className="min-w-0">
        <div className="mb-3 flex items-center gap-2">
          {Icon && (
            <span className={`grid size-8 shrink-0 place-items-center rounded-full ${tones[tone]}`}>
              <Icon size={16} strokeWidth={ICON_STROKE} aria-hidden="true" />
            </span>
          )}
          <p className="text-caption text-ink-3 truncate first-letter:uppercase">{label}</p>
        </div>
        <Figure value={value} {...(unit ? { unit } : {})} />
      </div>
      {aside}
    </div>
  )
}

/** Sélecteur à segments (période, etc.). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
  label: string
}) {
  return (
    <div role="group" aria-label={label} className="bg-sunken/70 flex rounded-full p-1">
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
            className={`text-caption min-h-11 flex-1 rounded-full font-bold transition-colors duration-150 ${
              selected ? 'bg-surface text-ink shadow-[var(--shadow-soft)]' : 'text-ink-3'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** Pastille de choix (durée, jour). */
export function Pill({
  selected,
  onClick,
  children,
}: {
  selected: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      className={`press text-body min-h-12 rounded-full font-bold ${
        selected ? 'bg-green text-surface' : 'bg-surface text-ink-2 shadow-[var(--shadow-soft)]'
      }`}
    >
      {children}
    </button>
  )
}
